import { Component, OnInit, Output, EventEmitter, Input, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { UtilsService } from 'core/services/utils.service';
import { LocalesService, ProfileService, StoreService } from 'core/services';
import { Group } from 'core/models/group';
import { DataTypes, TableColumn, TableDatasource } from '@gamelearn/skeletor/models';
import { Subscription } from 'rxjs';
import { REQUIRED_FIELD_BATCH_STUDENTS, REQUIRED_FIELD_BATCH_STUDENTS_BONUS_GROUP } from '../assets/constants';
import { StudentBatch } from '@models/StudentBatch';

@Component({
  selector: 'lms-students-batch-create',
  templateUrl: './students-batch-create.component.html',
  styleUrls: ['./students-batch-create.component.scss']
})
export class StudentsBatchCreateComponent implements OnInit, OnDestroy {
  @Input() public group: Group;
  @Input() public tooltipText: string;
  @Output() public studentsData: EventEmitter<any> = new EventEmitter<any>();
  public dataSource: TableDatasource;
  public studentBatchListId = 'STUDENT_BATCH_LIST';
  public toolbarConfig: any;
  public validators: any[] = [];
  public hasToReEnroll = true;
  private studentsBatchSubscription: Subscription = new Subscription();
  private touched: boolean = false;
  private hasBonusGroup = false;

  constructor(
    private profileService: ProfileService,
    private translateService: TranslateService,
    private localesService: LocalesService
  ) {}

  public ngOnInit(): void {
    this.toolbarConfig = {
      list: {
        listId: this.studentBatchListId,
        download: false,
        filterRow: false,
        hideSearch: true,
        hidePrint: true,
        hideItemsSelector: true,
        columnChooser: true,
        skeletor: true
      }
    };

    if (this.group) {
      this.hasBonusGroup = this.group.hasBonus;
    }

    this.studentsBatchSubscription = this.profileService.getLoggedUserRole$().subscribe((userRole: any) => {
      this.dataSource = this.getDataSource(this.studentBatchListId, userRole, new TableDatasource());
      StoreService.create(this.studentBatchListId, this.dataSource);
      this.addEmptyRow();
    });
  }

  public ngOnDestroy(): void {
    this.studentsBatchSubscription.unsubscribe();
  }

  public addEmptyRow(): void {
    this.touched = true;
    this.dataSource.refresh();
    this.addRow({ empty: true }, true);
  }

  public deleteRow(data: any, row: number): void {
    this.touched = true;
    this.validators.splice(row, 1);
    this.dataSource.docs.splice(row, 1);
    StoreService.update(this.studentBatchListId, this.dataSource);
    this.dataSource.refresh();
  }

  /**
   * Event listening from clipboard
   * @param event
   */
  public onPaste(event: any): void {
    const clipText = event.clipboardData.getData('Text');
    const data = clipText
      .trim()
      .replaceAll('\r\n', '\n')
      .replaceAll('\r', '\n')
      .split('\n')
      .map((row: string) => row.split('\t').filter((field: string) => !!field));

    if (data.length === 1 && data[0].length === 1) {
      return;
    } else {
      this.dataSource.docs = this.deleteEmptyRows(this.dataSource.docs);
      event.preventDefault();
      data.forEach((row: any) => {
        const range = {};
        this.dataSource.columns
          .filter((col: any) => !!col.source && col.visible)
          .forEach((col: any, index: number) => (range[col.source] = this.parseClipboardData(col.source, row[index])));
        this.addRow(range, true);
      });
      this.dataSource.refresh();
    }
  }
  // Ramon\tToledo\tnano@nano.com\tRamo\t88\t

  get hasTableErrors() {
    if (this.touched && this.validators.length) {
      return this.validators.some((validator) => !validator.valid);
    }
    return !this.validators.length;
  }

  public onSubmit(): void {
    const studentBatchList: StudentBatch[] = this.parseData(this.dataSource.docs);
    this.studentsData.next({ data: studentBatchList, hasToReEnroll: this.hasToReEnroll });
  }

  /**
   * Fill the data with the table
   * @param listId
   * @param userRole
   * @param studentsBatchDatasource
   */
  private getDataSource(listId: string, userRole: any, studentsBatchDatasource: any): TableDatasource {
    let dataSource = new TableDatasource({});
    this.validators = [];
    dataSource = new TableDatasource({
      docs: studentsBatchDatasource.docs,
      columns: this.getColumns(),
      limit: studentsBatchDatasource.limit,
      page: studentsBatchDatasource.page,
      pages: studentsBatchDatasource.pages,
      total: studentsBatchDatasource.total,
      pristine: false,
      storeState: {
        enabled: false,
        key: `${listId}_${userRole.name}`
      }
    });
    return dataSource;
  }

  /**
   * Get the columns of the table
   * @private
   */
  private getColumns(): TableColumn[] {
    return [
      {
        source: 'name',
        label: this.translateService.instant('nouns.firstName'),
        dataType: DataTypes.string,
        style: { width: '160px' },
        cellRenderer: this.getCellRenderer('name')
      },
      {
        source: 'surname',
        label: this.translateService.instant('nouns.surname'),
        dataType: DataTypes.string,
        allowFiltering: false,
        style: { width: '100px' },
        cellRenderer: this.getCellRenderer('surname')
      },
      {
        source: 'email',
        label: this.translateService.instant('nouns.email'),
        dataType: DataTypes.string, // email
        mandatory: true,
        hidden: true,
        style: { width: '110px' },
        cellRenderer: this.getCellRenderer('email')
      },
      {
        source: 'alias',
        label: this.translateService.instant('nouns.alias'),
        dataType: DataTypes.string,
        style: { width: '110px' },
        cellRenderer: this.getCellRenderer('alias')
      },
      {
        source: 'pid',
        label: this.translateService.instant('nouns.pid'),
        dataType: DataTypes.string,
        mandatory: this.hasBonusGroup,
        hidden: this.hasBonusGroup,
        style: { width: '110px' },
        cellRenderer: this.getCellRenderer('pid')
      },
      {
        source: 'lang',
        label: this.translateService.instant('nouns.lang'),
        style: { width: '120px' },
        cellRenderer: (renderer, value, data, row) => {
          const localesOptions = this.localesService.getExtendedOptions();
          return renderer`
            <gl-select
            style='width: 120px;'
            medium
            .value="${value || null}"
            .options="${localesOptions}"
            @onChange="${(ev) => this.onUpdateRow(ev, 'lang', row)}">
            </gl-select>
          
          `;
        }
      },
      {
        source: 'phone',
        label: this.translateService.instant('nouns.phone'),
        dataType: DataTypes.string,
        style: { width: '110px' },
        cellRenderer: this.getCellRenderer('phone')
      },
      {
        source: 'tag',
        label: this.translateService.instant('nouns.tags'),
        dataType: DataTypes.string,
        style: { width: '110px' },
        cellRenderer: this.getCellRenderer('tag')
      },
      {
        source: 'comments',
        label: this.translateService.instant('nouns.comments'),
        dataType: DataTypes.string,
        style: { width: '110px' },
        cellRenderer: this.getCellRenderer('comments')
      },
      {
        alignment: 'right',
        hidden: true,
        style: { width: '75px' },
        cellRenderer: (renderer, value, data, row) => {
          return renderer`<gl-button slot="target" icon icon-center="glicon-trash" @click="${() =>
            this.deleteRow(data, row)}"></gl-button>`;
        }
      }
    ];
  }

  private getCellRenderer(field: string): any {
    return (renderer: any, value: any, data: any, row: number) => {
      return renderer`
        <gl-input inline 
          type="string" 
          small
          .value="${value}"
          .errorMessage="${this.validate(field, value, row)}"
          @onChange="${(ev) => this.onUpdateRow(ev, field, row)}"
        >
        </gl-input>`;
    };
  }

  private onUpdateRow(event: CustomEvent, field: string, row: number): void {
    this.setEmptyRow(row);
    this.touched = true;
    this.validators[row].touched.add(field);
    // important to create new object to re-evaluate complete row
    const newData = { ...this.dataSource.docs[row] };
    newData[field] = event.detail.value;
    this.dataSource.docs[row] = newData;
    this.dataSource.refresh();
  }

  /**
   * Add a row in the table
   * @param data
   * @param dirty
   * @private
   */
  private addRow(data: any, dirty?: boolean): void {
    this.validators.push({ valid: false, touched: new Set(dirty ? Object.keys(data) : null), errors: {} });
    this.dataSource.docs.push(data);
    StoreService.update(this.studentBatchListId, this.dataSource);
  }

  /**
   * Validate required field cells
   * @param field
   * @param value
   * @param row
   * @private
   */
  private validate(field: string, value: any, row: number): string {
    const validator = this.validators[row];
    const requiredFields = !this.hasBonusGroup
      ? REQUIRED_FIELD_BATCH_STUDENTS
      : REQUIRED_FIELD_BATCH_STUDENTS_BONUS_GROUP;

    let errorMessage: string;
    if (requiredFields[field] && (value === null || !`${value}`.trim().length)) {
      errorMessage = this.translateService.instant('error.required');
    } else if (requiredFields[field] === 'email' && !UtilsService.EMAIL_REGEXP.test(value)) {
      errorMessage = this.translateService.instant('error.mail');
    } else if (field === 'tag' && !UtilsService.TAGS_REGEXP.test(value)) {
      errorMessage = this.translateService.instant('error.tags');
    }

    if (errorMessage) {
      validator.errors[field] = errorMessage;
    } else {
      delete validator.errors[field];
    }
    validator.valid = Object.values(validator.errors).length === 0;

    // only return error when field has been touched
    if (validator.touched.has(field)) {
      return errorMessage;
    }
  }

  /**
   * Parse data submit
   * @param studentBatchList
   * @private
   */
  private parseData(studentBatchList: StudentBatch[]) {
    return studentBatchList.map((student: StudentBatch) => {
      if (student.tag) {
        student.tags = student.tag
          .trim()
          .split(',')
          .map((tag: string) => {
            return tag.trim();
          });
      }
      delete student.tag;
      return student;
    });
  }

  /**
   * Parse clipboard data with row of table before pasted
   * @param columnName
   * @param clipboardRow
   * @private
   */
  private parseClipboardData(columnName: string, clipboardRow: string) {
    if (columnName === 'lang' && clipboardRow) {
      return clipboardRow.toLowerCase();
    }
    return clipboardRow;
  }

  /**
   * Delete empty rows of the table
   * @param studentBatchList
   * @private
   */
  private deleteEmptyRows(studentBatchList: StudentBatch[] | any[]): any[] {
    let numRowDeleted = 0;
    const tempRows: StudentBatch[] | any[] = [...studentBatchList];
    studentBatchList.map((row: any, index: number) => {
      if (row.empty) {
        this.validators.splice(index - numRowDeleted, 1);
        tempRows.splice(index - numRowDeleted, 1);
        numRowDeleted++;
      }
    });
    return tempRows;
  }

  /**
   * Empty row sets to object when value changed
   * @param rowNum
   * @private
   */
  private setEmptyRow(rowNum: number): void {
    if (this.dataSource.docs[rowNum]?.empty) this.dataSource.docs[rowNum] = {};
  }
}
